import React, { useState, useEffect, useRef } from "react";
import { Icon, Switch } from "../../";
import { IntegrationLogs, SetupModal } from "../../Business";
import { useIntegrationActions } from "../../../../hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { toast } from "sonner";

const SalesChannelIntegrationCard = ({
  integration,
  business,
  onBusinessUpdate,
  onIntegrationsUpdate,
}) => {
  const { name, logo, plugin } = integration;

  const queryClient = useQueryClient();
  const [showLogs, setShowLogs] = useState(false);
  const [externalId, setExternalId] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);
  const [pluginModal, setPluginModal] = useState({
    open: false,
    title: "",
    url: "",
  });
  const externalIdTimeoutRef = useRef(null);

  const {
    pluginSelected,
    loading,
    isIntegrationConnected,
    getButtonText,
    shouldHideSetupButton,
    handleButtonClick,
    handleClosePluginModal: handleCloseSetupModal,
    handleDisconnectBusiness,
    parseTemplate,
    getVariables,
  } = useIntegrationActions({
    business,
    onBusinessUpdate,
    onIntegrationsUpdate,
  });

  // Find business integration data
  const businessIntegration = business?.third_party_integrations?.find(
    (bi) => bi.third_party_integration_id === integration.id,
  );

  const isSetupSuccess = businessIntegration?.setup_status === "success";
  const isSetupFailed = businessIntegration?.setup_status === "failed";

  // Update local states when business integration changes
  useEffect(() => {
    if (businessIntegration) {
      setExternalId(businessIntegration.external_id || "");
      setIsEnabled(businessIntegration.enabled ?? false);
    }
  }, [businessIntegration]);

  // Push Menu Mutation
  const pushMenuMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(
        `/business/${business?.id}/third_party_integrations/${integration.id}/push_menu`,
      );
      return response;
    },
    onSuccess: () => {
      toast.success("Menu pushed successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to push menu");
    },
  });

  // Toggle Enabled Mutation
  const toggleEnabledMutation = useMutation({
    mutationFn: async (enabled) => {
      const response = await apiClient.post(
        `/business/${business?.id}/third_party_integrations/${integration.id}`,
        { enabled },
      );
      return response;
    },
    onMutate: async (enabled) => {
      // Capture previous data BEFORE applying optimistic update
      const previousData = business?.slug
        ? queryClient.getQueryData(["business-by-slug", business.slug])
        : null;

      // Optimistic update: update integration enabled status in business-by-slug cache
      if (business?.slug) {
        queryClient.setQueryData(
          ["business-by-slug", business.slug],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              third_party_integrations: (
                oldData.third_party_integrations || []
              ).map((bi) =>
                bi.third_party_integration_id === integration.id
                  ? { ...bi, enabled }
                  : bi,
              ),
            };
          },
        );
      }
      return {
        previousData,
      };
    },
    onSuccess: (data, enabled) => {
      // Invalidate businesses queries
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
      // Invalidate and refetch business-by-slug query if we have the slug
      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
        // Force refetch immediately
        queryClient.refetchQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }
      // Also invalidate businesses-products queries
      queryClient.invalidateQueries({
        queryKey: ["businesses-products"],
        exact: false,
      });
      toast.success(enabled ? "Integration enabled" : "Integration disabled");
    },
    onError: (error, enabled, context) => {
      // Revert optimistic update on error
      if (context?.previousData && business?.slug) {
        queryClient.setQueryData(
          ["business-by-slug", business.slug],
          context.previousData,
        );
      }
      setIsEnabled(!enabled);
      toast.error(error.message || "Failed to update integration status");
    },
  });

  // Update External ID Mutation
  const updateExternalIdMutation = useMutation({
    mutationFn: async (external_id) => {
      const response = await apiClient.post(
        `/business/${business?.id}/third_party_integrations/${integration.id}`,
        { external_id },
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
      // Invalidate business-by-slug query if we have the slug
      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }
      toast.success("External ID updated successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update external ID");
    },
  });

  const handlePushMenu = () => {
    pushMenuMutation.mutate();
  };

  const handleToggleEnabled = (checked) => {
    setIsEnabled(checked);
    toggleEnabledMutation.mutate(checked);
  };

  const handleExternalIdChange = (value) => {
    setExternalId(value);

    // Clear existing timeout
    if (externalIdTimeoutRef.current) {
      clearTimeout(externalIdTimeoutRef.current);
    }

    // Set new timeout to update after 1 second of no typing
    externalIdTimeoutRef.current = setTimeout(() => {
      if (value !== (businessIntegration?.external_id || "")) {
        updateExternalIdMutation.mutate(value);
      }
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (externalIdTimeoutRef.current) {
        clearTimeout(externalIdTimeoutRef.current);
      }
    };
  }, []);

  const handleOpenLogs = () => {
    setShowLogs(true);
  };

  const handleCloseLogs = () => {
    setShowLogs(false);
  };

  const handleSetupButtonClick = (e) => {
    const modifiedIntegration =
      isSetupSuccess && integration?.plugin?.links?.general_settings
        ? {
            ...integration,
            plugin: {
              ...integration.plugin,
              setup_url: integration.plugin.links.general_settings,
            },
          }
        : integration;

    handleButtonClick(modifiedIntegration, e);
  };

  const handleOpenPluginDetails = (e, linkKey) => {
    e.stopPropagation();

    if (!plugin?.links?.[linkKey]) return;

    const variables = getVariables();
    const url = parseTemplate(plugin.root + plugin.links[linkKey], variables);

    setPluginModal({
      open: true,
      title: plugin.name,
      url,
    });
  };

  const handleClosePluginModal = () => {
    setPluginModal({ open: false, title: "", url: "" });
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const isConnected = isIntegrationConnected(integration);
  const buttonText = getButtonText(integration);
  const hideButton = shouldHideSetupButton(integration);

  return (
    <div
      className={`hover:bg-gray-750 flex flex-col justify-between rounded-xl border border-gray-700 bg-gray-800 p-6 transition-all duration-200 hover:border-gray-600 ${
        isConnected ? "lg:col-span-2" : ""
      }`}
    >
      <div className="flex flex-col">
        {/* Header Section */}
        <div className="mb-0 flex items-start space-x-4">
          {logo && (
            <div className="flex-shrink-0">
              <img
                src={logo}
                alt={name}
                className="h-14 w-14 rounded-xl border border-gray-600 object-cover"
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h5 className="text-xl font-semibold text-white">{name}</h5>
                  {isConnected && (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        isSetupSuccess
                          ? "border border-green-500/30 bg-green-900/20 text-green-400"
                          : isSetupFailed
                            ? "border border-red-500/30 bg-red-900/20 text-red-400"
                            : "border border-gray-600 bg-gray-800 text-gray-400"
                      }`}
                    >
                      {isSetupSuccess
                        ? "Setup Complete"
                        : isSetupFailed
                          ? "Setup Failed"
                          : "Setup Pending"}
                    </span>
                  )}
                </div>
                <p className="mb-3 text-sm text-gray-400">
                  {plugin?.description || "Sales Channel Integration"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Section */}
      <div className="space-y-4">
        {/* Primary Action Button - Only show when NOT connected */}
        {!hideButton && !isConnected && (
          <div className="flex justify-center">
            <button
              onClick={(e) => handleSetupButtonClick(e)}
              disabled={loading}
              className={`inline-flex w-full items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                !integration?.plugin_id
                  ? "border border-transparent bg-purple-600 text-white hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:outline-none"
                  : "border border-transparent bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none"
              }`}
            >
              {loading ? (
                <div className="mr-2 inline h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              ) : (
                <Icon
                  name={!integration?.plugin_id ? "Download" : "Plus"}
                  className="mr-2 inline h-4 w-4"
                />
              )}
              {buttonText}
            </button>
          </div>
        )}

        {/* Sales Channel Actions - Show when connected */}
        {isConnected && (
          <div className="space-y-4 border-t border-gray-700 pt-4">
            {/* External ID Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                External ID
              </label>
              <input
                type="text"
                value={externalId}
                onChange={(e) => handleExternalIdChange(e.target.value)}
                disabled={updateExternalIdMutation.isPending}
                className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
                placeholder="Enter external ID..."
              />
              <p className="text-xs text-gray-400">
                Changes are saved automatically after 1 second
              </p>
            </div>

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between rounded-lg bg-gray-900/50 p-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-300">
                  Status
                </label>
                <p className="mt-1 text-xs text-gray-400">
                  {isEnabled
                    ? "Integration is enabled"
                    : "Integration is disabled"}
                </p>
              </div>
              <Switch
                checked={isEnabled}
                onCheckedChange={handleToggleEnabled}
                disabled={toggleEnabledMutation.isPending}
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {/* Push Menu Button */}
              <button
                onClick={handlePushMenu}
                disabled={pushMenuMutation.isPending}
                className="inline-flex w-full items-center justify-center rounded-lg border border-blue-500/30 bg-blue-900/20 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:border-blue-400/50 hover:bg-blue-900/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pushMenuMutation.isPending ? (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-blue-400"></div>
                ) : (
                  <Icon name="Upload" className="mr-2 h-4 w-4" />
                )}
                Push Menu
              </button>

              {/* Dynamic Plugin Links Buttons */}
              {plugin?.links &&
                Object.keys(plugin.links).map((key) => (
                  <button
                    key={key}
                    onClick={(e) => handleOpenPluginDetails(e, key)}
                    disabled={loading}
                    className="inline-flex w-full items-center justify-center rounded-lg border border-blue-500/30 bg-blue-900/20 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:border-blue-400/50 hover:bg-blue-900/30 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon name="ExternalLink" className="mr-2 h-4 w-4" />
                    {capitalize(key.replace(/_/g, " "))}
                  </button>
                ))}

              {/* View Logs Button */}
              <button
                onClick={handleOpenLogs}
                className="inline-flex w-full items-center justify-center rounded-lg border border-blue-500/30 bg-blue-900/20 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:border-blue-400/50 hover:bg-blue-900/30"
              >
                <Icon name="FileText" className="mr-2 h-4 w-4" />
                View Logs
              </button>

              {/* Disconnect Button */}
              <button
                onClick={() =>
                  handleDisconnectBusiness(integration?.id, () => {
                    onBusinessUpdate && onBusinessUpdate();
                    onIntegrationsUpdate && onIntegrationsUpdate();
                  })
                }
                disabled={loading}
                className="inline-flex w-full items-center justify-center rounded-lg border border-red-500/30 bg-red-900/20 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:border-red-400/50 hover:bg-red-900/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-red-400"></div>
                ) : (
                  <Icon name="X" className="mr-2 h-4 w-4" />
                )}
                Disconnect Sales Channel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Integration Logs Modal */}
      <IntegrationLogs
        isOpen={showLogs}
        onClose={handleCloseLogs}
        model="Business"
        modelId={business?.id}
        pluginId={integration?.plugin_id}
      />

      {/* Setup Modal for Connect/Install actions */}
      <SetupModal
        isOpen={pluginSelected.open}
        onClose={handleCloseSetupModal}
        title={pluginSelected.title}
        url={pluginSelected.url}
        parseTemplate={parseTemplate}
        variables={getVariables()}
      />

      {/* Plugin Links Modal */}
      <SetupModal
        isOpen={pluginModal.open}
        onClose={handleClosePluginModal}
        title={pluginModal.title}
        url={pluginModal.url}
        parseTemplate={parseTemplate}
        variables={getVariables()}
      />
    </div>
  );
};

export default SalesChannelIntegrationCard;
